import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/apiClient';
import { toast } from 'sonner';

export const seatKeys = {
  all: ['seats'] as const,
  plans: () => [...seatKeys.all, 'plans'] as const,
  summary: (organizationId: number) => [...seatKeys.all, 'summary', organizationId] as const,
  accessStatus: () => [...seatKeys.all, 'access-status'] as const,
};

/**
 * Hook to fetch the globally available seat plans (Small/Medium/Large).
 * Not scoped to an organization.
 */
export const useSeatPlansQuery = () => {
  return useQuery({
    queryKey: seatKeys.plans(),
    queryFn: () => apiClient.getSeatPlans(),
    staleTime: 10 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

/**
 * Hook to fetch an organization's seat summary: available/active seat counts,
 * good-standing status, purchased batches, and per-member seat status.
 */
export const useOrganizationSeatSummaryQuery = (organizationId: number) => {
  return useQuery({
    queryKey: seatKeys.summary(organizationId),
    queryFn: () => apiClient.getOrganizationSeatSummary(organizationId),
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    enabled: !!organizationId,
  });
};

/**
 * Hook to check whether the current user has Projects/Reports access in at least one
 * of their organizations (an active seat and the org(s) not over-pool). Used to gate
 * the Projects/Reports sidebar items.
 */
export const useSeatAccessStatusQuery = (enabled: boolean = true) => {
  return useQuery({
    queryKey: seatKeys.accessStatus(),
    queryFn: () => apiClient.getSeatAccessStatus(),
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    enabled,
  });
};

/**
 * Hook to create a Stripe Checkout Session for a seat plan purchase.
 */
export const useCreateSeatCheckoutMutation = () => {
  return useMutation({
    mutationFn: ({ organizationId, seatPlanId }: { organizationId: number; seatPlanId: number }) =>
      apiClient.createSeatCheckoutSession(organizationId, seatPlanId),
    onSuccess: (data) => {
      // Redirect to Stripe Checkout
      window.location.href = data.url;
    },
    onError: (error) => {
      console.error('Failed to create seat checkout session:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to start checkout');
    },
  });
};

/**
 * Hook to activate a seat for an organization member.
 * Invalidates the seat summary for that organization on success.
 */
export const useActivateSeatMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ organizationId, userId }: { organizationId: number; userId: number }) =>
      apiClient.activateSeat(organizationId, userId),
    onSuccess: (_, { organizationId }) => {
      queryClient.invalidateQueries({ queryKey: seatKeys.summary(organizationId) });
      queryClient.invalidateQueries({ queryKey: seatKeys.accessStatus() });
    },
    onError: (error) => {
      console.error('Failed to activate seat:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to activate seat');
    },
  });
};

/**
 * Hook to deactivate a seat for an organization member.
 * Invalidates the seat summary for that organization on success.
 */
export const useDeactivateSeatMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ organizationId, userId }: { organizationId: number; userId: number }) =>
      apiClient.deactivateSeat(organizationId, userId),
    onSuccess: (_, { organizationId }) => {
      queryClient.invalidateQueries({ queryKey: seatKeys.summary(organizationId) });
      queryClient.invalidateQueries({ queryKey: seatKeys.accessStatus() });
    },
    onError: (error) => {
      console.error('Failed to deactivate seat:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to deactivate seat');
    },
  });
};
